import { sameCompany } from "./companyRules";
import { keepsVehicle } from "./masterDrift";

/**
 * Pure helpers for choosing the vehicle(s) of an Online Policy:
 *   - SINGLE company: one Make & Model row; OD and TP are that row's company.
 *   - MULTIPLE company: one Combined Make & Model record, which pairs the OD
 *     insurer's row with the TP insurer's row — a vehicle each.
 *
 * The form keeps `selectedMakeModel` = the OD row in both modes, so everything
 * that already follows it (discount, IDV range, nil dep, model-not-found) is
 * untouched; the TP side is read from the combo's TP row.
 */

/** One Make & Model row as the form's dropdown option (the same shape in both modes). */
export const toVehicleOption = (item) => ({
  _id: item._id,
  _disabledInMaster: !item.isEnabled,
  make: item.make,
  model: item.model,
  idvRangeFrom: item.idvRangeFrom,
  idvRangeTo: item.idvRangeTo,
  cc: item.cc,
  company: item.company,
  discount: item.discount,
  vehicleVariant: item.vehicleVariant || "",
  // Vehicles missing from the Reliance portal's list: the portal
  // derives the IDV from this selling price instead.
  modelNotFound: !!item.modelNotFound,
  manufacturerSellingPrice: item.manufacturerSellingPrice,
  // Per-vehicle Zero Depreciation value and TPPD availability.
  nilDipPrice: item.nilDipPrice,
  tppdRequired: !!item.tppdRequired,
  paCoverMinAmount: item.paCoverMinAmount,
  fuelType: item.fuelType || "",
  seatingCapacity: item.seatingCapacity || "",
  label: `${item.make} - ${item.model}`,
  value: `${item.make} - ${item.model}`,
  // Everything the dropdown search looks at, flattened once rather than rebuilt
  // on every keystroke.
  searchText: [item.make, item.model, item.company, item.vehicleVariant, item.cc, item.fuelType]
    .filter(Boolean)
    .join(" ")
    .toLowerCase(),
});

/** A Combined Make & Model record (from GET /make-model-combo) as a dropdown option. */
export const comboOption = (combo) => {
  const odRow = combo.odRow ? toVehicleOption(combo.odRow) : null;
  const tpRow = combo.tpRow ? toVehicleOption(combo.tpRow) : null;
  return {
    _id: String(combo._id),
    displayName: combo.displayName || "",
    odCompany: combo.odCompany,
    tpCompany: combo.tpCompany,
    odMakeModel: String(combo.odMakeModel),
    tpMakeModel: String(combo.tpMakeModel),
    odRow,
    tpRow,
    isEnabled: combo.isEnabled !== false,
    usable: combo.usable !== false,
    issues: combo.issues || [],
    label: combo.displayName || "",
    searchText: [combo.displayName, combo.odCompany, combo.tpCompany, odRow?.searchText, tpRow?.searchText]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  };
};

/**
 * Which combined records the user can pick: the enabled, usable ones, narrowed by
 * ONE of two rules:
 *
 *  1. The user has Combined Make & Models assigned (`assignedComboIds`, from their
 *     Edit User > Combined Make & Model tab): only those. This takes over from
 *     rule 2. An assignment whose combo no longer exists (deleted or disabled, so
 *     it is not in `options`) does not count — a user whose every assignment is
 *     gone is not left with an empty dropdown, they fall back to rule 2.
 *  2. Otherwise, like the single dropdown: only combos whose OD Make & Model is
 *     assigned to them when they have any Make & Model assignments, and all of
 *     them when they have none.
 *
 * The saved policy's own combo always stays resolvable (`keepId`) even if it has
 * since been disabled or un-assigned.
 */
export const visibleCombos = (
  options,
  { assignedIds, hasAssignments, assignedComboIds, keepId } = {}
) => {
  const list = options || [];
  const comboIds = assignedComboIds instanceof Set ? assignedComboIds : new Set();
  const restrictToCombos = list.some((option) => comboIds.has(String(option._id)));
  return list.filter((option) => {
    if (keepId && String(option._id) === String(keepId)) return true;
    if (!option.isEnabled || !option.usable) return false;
    if (restrictToCombos) return comboIds.has(String(option._id));
    if (hasAssignments && !(assignedIds && assignedIds.has(String(option.odMakeModel)))) return false;
    return true;
  });
};

/** The two companies a selection means: the combo's OD and TP, or the one row's company for both. */
export const routingFor = ({ combo, row }) =>
  combo
    ? { od: combo.odCompany || "", tp: combo.tpCompany || "" }
    : { od: row?.company || "", tp: row?.company || "" };

/** A saved policy's TP vehicle snapshot, shaped like a Make & Model option so it can be priced and shown. */
export const tpRowFromSnapshot = (tpVehicle, company) =>
  tpVehicle
    ? {
        _id: tpVehicle.makeModel,
        make: tpVehicle.vehicleMake,
        model: tpVehicle.vehicleModel,
        vehicleVariant: tpVehicle.vehicleVariant || "",
        cc: String(tpVehicle.vehicleCC ?? ""),
        company: company || "",
        modelNotFound: !!tpVehicle.modelNotFound,
        manufacturerSellingPrice: tpVehicle.manufacturerSellingPrice,
        fuelType: tpVehicle.fuelType || "",
        seatingCapacity: tpVehicle.seatingCapacity || "",
        label: `${tpVehicle.vehicleMake} - ${tpVehicle.vehicleModel}`,
        value: `${tpVehicle.vehicleMake} - ${tpVehicle.vehicleModel}`,
      }
    : null;

/**
 * Which mode a SAVED policy opens in, from the policy alone:
 *   - sold from a combined record -> Multiple + that record;
 *   - two companies but NO record (sold before combined records existed) ->
 *     Multiple too, flagged `_legacyMultiple`: it keeps its saved companies and
 *     vehicle and needs no record until the vehicle is changed;
 *   - anything else -> Single.
 */
export const selectionFromPolicy = (policy) => {
  const comboId = policy?.makeModelCombo ? String(policy.makeModelCombo) : "";
  const od = policy?.settings?.ODCompany || policy?.odCompany || policy?.company || "";
  const tp = policy?.settings?.TPCompany || policy?.tpCompany || policy?.company || od;
  const twoCompanies = policy?.settings?.isMultipleCompany === true || (!!od && !!tp && !sameCompany(od, tp));
  const multiple = !!comboId || twoCompanies;
  return {
    companyChoice: multiple ? "multiple" : "single",
    makeModelCombo: comboId,
    _legacyMultiple: multiple && !comboId,
  };
};

/**
 * The record a saved policy was sold from, rebuilt from the policy itself (its
 * name, its companies, its TP vehicle snapshot) so the form can show and price
 * it without the master. Null for a single-company policy and for an old
 * multi-company one (no record).
 */
export const comboFromPolicy = (policy) => {
  if (!policy?.makeModelCombo) return null;
  const od = policy.settings?.ODCompany || policy.odCompany || policy.company || "";
  const tp = policy.settings?.TPCompany || policy.tpCompany || "";
  return {
    _id: String(policy.makeModelCombo),
    displayName: policy.makeModelComboName || "",
    odCompany: od,
    tpCompany: tp,
    odMakeModel: String(policy.makeModel || ""),
    tpMakeModel: String(policy.tpVehicle?.makeModel || ""),
    odRow: null, // the OD vehicle is the policy's own top-level vehicle
    tpRow: tpRowFromSnapshot(policy.tpVehicle, tp),
    isEnabled: true,
    usable: true,
    issues: [],
    label: policy.makeModelComboName || "",
    fromSnapshot: true,
  };
};

/**
 * What the form sends about a Combined Make & Model. Only the record's id goes —
 * the server loads it, checks it and sets both companies and the TP vehicle from it.
 *   tpVehicleCC       the CC the TP premium was priced on (the server refuses a mismatch,
 *                     so a premium priced on another TP vehicle can never be saved)
 *   refreshTpVehicle  an edit that has let go of the saved vehicle ("use latest master
 *                     values", or a re-pick) takes the TP row's latest values; an edit
 *                     that keeps the vehicle keeps the TP vehicle it was sold with
 * A single-company policy, and an old multi-company one that is still untouched (no
 * record), send an empty id.
 */
export const comboPayloadFields = ({ policyData, calculationResult, isEditMode, savedSnapshot }) => {
  const comboId =
    policyData?.companyChoice === "multiple" && policyData?.makeModelCombo ? String(policyData.makeModelCombo) : "";
  if (!comboId) return { makeModelCombo: "" };
  return {
    makeModelCombo: comboId,
    ...(calculationResult?.tpCc ? { tpVehicleCC: calculationResult.tpCc } : {}),
    ...(isEditMode && !keepsVehicle(savedSnapshot, policyData.makeModel) ? { refreshTpVehicle: true } : {}),
  };
};

/** The vehicle priced for THIRD PARTY: the combo's TP row in Multiple mode, nothing (= the one vehicle) in Single. */
export const tpVehicleOf = ({ mode, combo }) => (mode === "multiple" && combo ? combo.tpRow || null : null);

const positive = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * The lowest PA Cover amount that may be typed when an insurer sells the PA (Brisk
 * does not use this: its minimum is always ₹177). One row: its own minimum when it
 * has one, otherwise the long-standing default (exactly what the form always did).
 * Two rows: the HIGHER of the two, so the floor does not jump when the PA company
 * is switched between them.
 */
export const paFloorOf = (rows, fallback) => {
  const floors = (rows || []).filter(Boolean).map((row) => positive(row.paCoverMinAmount) || fallback);
  return floors.length ? Math.max(...floors) : fallback;
};

/** Whether TPPD Restrict may be offered: it is a third-party item, so the TP row decides in Multiple mode. */
export const tppdAvailableOf = ({ mode, odRow, tpRow }) =>
  mode === "multiple" && tpRow ? !!tpRow.tppdRequired : !!odRow?.tppdRequired;

const round2 = (n) => Number(Number(n).toFixed(2));
const known = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

/**
 * The figures the Motor Calculation page ends on, read from the engine result:
 *
 *   totalA     Total A   own damage — the OD insurer's side
 *   totalB     Total B   the engine's whole second column: third party AND PA to Owner Driver
 *   pa         PA Cover  the PA to Owner Driver amount inside Total B (0 without PA Cover)
 *   tpPremium  Total B without PA — third party alone, the TP insurer's side
 *   net        Net Premium: Total A + Total B, i.e. own damage + third party + PA Cover
 *   gst        GST @ 18% of the net
 *   final      Final Premium (net + GST, to the whole rupee)
 *
 * PA Cover is sold by its OWN company (Brisk, or an insurer), so a policy with two insurers shows
 * it on a line of its own and the TP insurer's panel shows `tpPremium` — the same amounts the
 * wallets are charged (companyRules.premiumSplit takes PA out of Total B the same way).
 *
 * Nothing is worked out here: these are the engine's own fields, so the two insurer panels, the
 * breakup dialog and the Motor Calculation page show the same numbers. A figure the result does
 * not carry (a premium saved before it was stored) is null — "not known", never 0.
 *
 * `mismatchPaise` is Total A + Total B minus the net, in paise. It is 0 for every fresh
 * calculation — the engine's net IS that sum, so the difference is taken before any rounding and
 * cannot be a stray paisa. Anything else is a saved premium whose parts do not add up, which the
 * form shows instead of hiding.
 */
export const premiumTotals = (result) => {
  if (!result) return null;
  const totalA = known(result.totalAodPremium);
  const totalB = known(result.totalBodPremium);
  const net = known(result.netPremiumAB);
  const pa = known(result.pAOwnerDriver) || 0;
  return {
    totalA,
    totalB,
    pa,
    tpPremium: totalB === null ? null : round2(totalB - pa),
    net,
    gst: known(result.tax),
    final: known(result.finalPremium),
    mismatchPaise:
      totalA === null || totalB === null || net === null ? 0 : Math.round((totalA + totalB - net) * 100) || 0,
  };
};
